import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronDown, ChevronRight, UserCircle2 } from "lucide-react";
import { Helmet } from "react-helmet-async";
import Navbar from "../Landing/Navbar";
import { sendOtp } from "../services/api";
import countryCodes from "../data/countryCodes";
import CountryCodePicker from "../Components/CountryCodePicker";

const profileOptions = ["Myself", "Son", "Daughter", "Brother", "Sister", "Relative", "Friend"];

const Register = () => {
  const navigate = useNavigate();
  const [profileType, setProfileType] = useState(() =>
    sessionStorage.getItem("karankiRegisterDraft.profileType") || "Myself"
  );
  const [name, setName] = useState(() =>
    sessionStorage.getItem("karankiRegisterDraft.name") || ""
  );
  const [phone, setPhone] = useState(() =>
    sessionStorage.getItem("karankiRegisterDraft.phone") || ""
  );
  const [countryCode, setCountryCode] = useState(() =>
    sessionStorage.getItem("karankiRegisterDraft.countryCode") || "91"
  );
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const selectedCountry =
    countryCodes.find((c) => c.code === countryCode) ||
    countryCodes.find((c) => c.code === "91");

  useEffect(() => {
    sessionStorage.setItem("karankiRegisterDraft.profileType", profileType);
    sessionStorage.setItem("karankiRegisterDraft.name", name);
    sessionStorage.setItem("karankiRegisterDraft.phone", phone);
    sessionStorage.setItem("karankiRegisterDraft.countryCode", countryCode);
  }, [profileType, name, phone, countryCode]);

  const placeholder = profileType === "Myself" ? "Your name" : `${profileType}'s name`;

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");

    if (!phone || phone.length < 7 || phone.length > selectedCountry.maxDigits) {
      setError(`Enter a valid ${selectedCountry.maxDigits}-digit mobile number.`);
      return;
    }

    if (!name.trim()) {
      setError("Please enter a name.");
      return;
    }

    const formattedPhone = `${countryCode}${phone}`;
    setLoading(true);

    try {
      const { sessionId, existingUser } = await sendOtp(formattedPhone, "register");

      if (existingUser) {
        setError("This mobile number is already registered. Please login instead.");
        setLoading(false);
        return;
      }

      sessionStorage.setItem(
        "karankiOtpState",
        JSON.stringify({
          sessionId,
          phone: formattedPhone,
          profileType,
          name,
          authMode: "register",
        })
      );
      navigate("/verify-otp");
    } catch (err) {
      setError(err.message || "Unable to send OTP. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Register | Karanki Matrimony</title>
        <meta
          name="description"
          content="Create your profile on Karanki Matrimony and verify your mobile number with OTP."
        />
        <link rel="canonical" href="https://www.karankimatrimony.com/register" />
      </Helmet>

      <Navbar />

      <main className="flex min-h-[calc(100vh-80px)] items-center justify-center px-4 py-10">
        <motion.section
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
          <div className="rounded-3xl border border-sand-200 bg-white p-6 shadow-soft sm:p-8">
            <h1 className="text-center text-2xl font-semibold text-ink-800">Register</h1>

            <form onSubmit={handleSubmit} className="mt-6 space-y-5">
              <div>
                <label className="field-label">Profile created for</label>
                <div className="relative">
                  <select
                    value={profileType}
                    onChange={(e) => setProfileType(e.target.value)}
                    className="input appearance-none pr-10"
                  >
                    {profileOptions.map((opt) => (
                      <option key={opt} value={opt}>
                        {opt}
                      </option>
                    ))}
                  </select>
                  <ChevronDown
                    size={16}
                    className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-brand-600"
                  />
                </div>
              </div>

              <div>
                <label className="field-label">Name</label>
                <div className="input-group">
                  <UserCircle2 size={17} className="text-ink-400" />
                  <input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder={placeholder}
                    autoComplete="name"
                  />
                </div>
              </div>

              <div>
                <label className="field-label">Mobile Number</label>
                <div className="input-group">
                  <CountryCodePicker value={countryCode} onChange={setCountryCode} />
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value.replace(/\D/g, ""))}
                    onFocus={(e) => e.target.scrollIntoView({ behavior: "smooth", block: "center" })}
                    placeholder="Enter mobile number"
                    maxLength={selectedCountry.maxDigits}
                    inputMode="numeric"
                    autoComplete="tel"
                  />
                </div>
              </div>

              {error && (
                <motion.div
                  initial={{ opacity: 0, y: -4 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="rounded-2xl border border-rose-100 bg-rose-50 px-4 py-3 text-[13px] text-rose-700"
                >
                  {error}
                </motion.div>
              )}

              <button type="submit" disabled={loading} className="btn-primary w-full py-4 text-[15px]">
                {loading ? "Sending OTP..." : "Continue"}
                {!loading && <ChevronRight size={16} />}
              </button>
            </form>

            <p className="mt-5 text-center text-sm text-ink-500">
              Already have an account?{" "}
              <Link to="/login" className="font-medium text-brand-600 hover:underline">
                Login
              </Link>
            </p>
          </div>
        </motion.section>
      </main>
    </div>
  );
};

export default Register;
